import {createAsyncThunk, createEntityAdapter, createSlice, createSelector} from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api"
import {
  clientVendorsByClient,
  updateVENDOR,
  createVENDOR,
  createINSURANCEAGENT,
  createCLIENTVENDORS,
  updateStarredVendor,
  createPROJECTVENDORS,
  deletePROJECTVENDORS,
  updateVendorStatus,
  updateVendorNotificationStatus,
  createVENDORCOVERAGE,
  updateVENDORCOVERAGE,
  updateINSURANCEAGENT,
  deleteVENDORCOVERAGE,
  deleteCERTIFICATE,
  getCLIENTVENDORS,
  updateCERTIFICATE,
  createCERTIFICATE,
  updateCERTIFICATECOVERAGE,
  createCERTIFICATECOVERAGE,
  deleteCERTIFICATECOVERAGE,
  getCERTIFICATE,
  createEMAILNOTIFICATIONS,
  createACORD25SCAN,
  updateACORD25SCAN,
  certificateByClientVendor,
  deleteVENDOR,
  deleteCLIENTVENDORS
} from "graphql/vendor"
import {
  deleteCertificateFile,
  deleteCertificateFromS3,
  uploadCertificateFile,
  copyScannedAcord25,
  uploadCertificateSupplementalCoiFile,
  deleteCertificateSupplementalCoiFile,
} from "services/appResources"
import {certificateByClient} from "../graphql/client";
import cognito_groups from "constants/cognito_groups";
import { usToAWSDate } from "utils/dateUtils";
import { Vendorstatus } from "models";
import { logVendorActivity } from "services/activity";

const adapter = createEntityAdapter()
const initialState = adapter.getInitialState({
  status: "idle",
  vendors: null,
  certificates: {}, // cache each certificate by id for quick access

  // certs for a single vendor
  vendorCertificates: null,
  paginatedVendorCertificates: null,
  vendorCertificatesCount: 0,
})

const API = generateClient();

function getFirstExp (coverages) {
  if(coverages?.length) {
    return coverages.map(coverage => coverage.policyExp)
      .flat().filter(exp => exp)
      .sort((a,b) => {
        if( new Date(a) > new Date(b) ) {
          return -1
        } else {
          return 1
        }
      })
      .pop() || null
  }
  return null
}

async function createVendorProjects(projectsToAdd, clientVendorID) {
  if(projectsToAdd?.length) {
    await Promise.all(projectsToAdd.map(async(projectToAdd, index) => {
      projectToAdd && await API.graphql({
        query: createPROJECTVENDORS,
        variables: {
          input: {
            projectID: projectToAdd,
            clientVendorID,
          }
        }
      })
      return Promise.resolve()
    }))
  }
}

async function deleteVendorProjects(projectsToDelete) {
  if(projectsToDelete?.length) {
    const response = await Promise.all(projectsToDelete.map(async (projectToDelete) => {
      const deleteOneresponse = await API.graphql({
        query: deletePROJECTVENDORS,
        variables: {
          input: {
            id: projectToDelete,
          }
        }
      })
      Promise.resolve()
      return {
       success:  !!deleteOneresponse.data.deletePROJECTVENDORS,
       id: projectToDelete
      }
    }))
    return response;
  }
}

async function updateVendorProjects(vendorData) {
  const { clientVendorID, defaultProject } = vendorData
  const selectedProjects = new Set(vendorData.selectedProjects)
  const existingProjects = vendorData?.projects?.items
  let existingDefaultProject = false
  let hasOtherProjects = false
  const projectsToDelete = []
  const projectsDeleted = []
  if(existingProjects?.length) {
    existingProjects.forEach(existingProject => {
      if(selectedProjects.has(existingProject.projectID)) {
        selectedProjects.delete(existingProject.projectID)

        if(existingProject.projectID === defaultProject?.id) {
          selectedProjects.delete(existingProject.projectID)
          existingDefaultProject = existingProject
        } else {
          hasOtherProjects = true
        }

      } else {
        projectsToDelete.push(existingProject.id) // contains the ProjectVendor ID
        projectsDeleted.push(existingProject.projectID) // contains the actual Project's ID
      }
    })
  }

  if(!existingDefaultProject && (!hasOtherProjects && !selectedProjects.size)) {
    if(defaultProject) {
      selectedProjects.add(defaultProject.id)
    }
  } else if(existingDefaultProject && (hasOtherProjects || selectedProjects.size !== 0)) {
    projectsToDelete.push(existingDefaultProject.id)
    projectsDeleted.push(existingDefaultProject.projectID)
  }

  const projectsToAdd = Array.from(selectedProjects)
  await createVendorProjects(projectsToAdd, clientVendorID)
  await deleteVendorProjects(projectsToDelete)
  return {
    projectsAdded: projectsToAdd,
    projectsDeleted,
  }
}

const updateOneVendor = (state, action) => {
  state.status = 'success'
  state.vendors = state.vendors.map(oneVendor => {
    if(oneVendor.id === action?.payload?.clientVendor?.id) {
      return action.payload.clientVendor
    }
    return oneVendor
  })
}

const statusLoading = state => {
  state.status = 'loading'
}

const statusFailure = state => {
  state.status = 'failure'
}

const actions = {
  fetchVendors: createAsyncThunk("vendor/fetchVendors", async (clientID, { getState }) => {
    const state = getState();
    const { loggedInUser } = state.user;
    const hasSubcontractorFiltering = [cognito_groups.ClientPMRole, cognito_groups.Subcontractor].includes(loggedInUser?.role);
    
    const userAssignedVendors = loggedInUser?.assignedVendors || [];

    const variables = {
      clientID,
    };

    // Add filter only if user is ClientPM and has assigned vendors
  if (hasSubcontractorFiltering && userAssignedVendors.length > 0) {
    variables.filter = {
      or: userAssignedVendors.map(vendorId => ({
        id: { eq: vendorId }
      }))
    };
  }


    const { data } = await API.graphql({
      query: clientVendorsByClient,
      variables,
    });
    
    return {
      vendors: data?.clientVendorsByClient?.items || [],
    };
  }),
  fetchVendor: createAsyncThunk("vendor/fetchVendor", async (clientVendorID) => {
    const { data } = await API.graphql({
      query: getCLIENTVENDORS,
      variables: {
        id: clientVendorID,
      },
    });
    return {
      success: true,
      clientVendor: data?.getCLIENTVENDORS,
    };
  }),
  updateVendor: createAsyncThunk("vendor/updateVendor", async (vendorData) => {
    const { id, name, useEntityName, entityName, email, additionalEmails, phone, logo, address1, address2, city, state, zip, insuranceAgent } = vendorData;
    let insuranceAgentResponse;
    if (insuranceAgent?.id) {
      await API.graphql({
        query: updateINSURANCEAGENT,
        variables: {
          input: {
            id: insuranceAgent?.id,
            name: insuranceAgent?.name,
            email: insuranceAgent?.email,
            phone: insuranceAgent?.phone,
            address1: insuranceAgent?.address1,
            address2: insuranceAgent?.address2,
            city: insuranceAgent?.city,
            state: insuranceAgent?.state,
            zip: insuranceAgent?.zip,
          },
        },
      });
    } else if (insuranceAgent?.name && insuranceAgent?.email) {
      insuranceAgentResponse = await API.graphql({
        query: createINSURANCEAGENT,
        variables: {
          input: {
            ...insuranceAgent,
          },
        },
      });
    }

    const insuranceAgentID = insuranceAgentResponse?.data.createINSURANCEAGENT?.id;
    await API.graphql({
      query: updateVENDOR,
      variables: {
        input: {
          id,
          name,
          useEntityName,
          entityName,
          email,
          additionalEmails,
          phone,
          logo,
          address1,
          address2,
          city,
          state,
          zip,
          ...(insuranceAgentID && { vENDORInsuranceAgentId: insuranceAgentID }),
        },
      },
    });
    const projectsUpdateResult = await updateVendorProjects(vendorData);
    const { data } = await API.graphql({
      query: getCLIENTVENDORS,
      variables: {
        id: vendorData.clientVendorID,
      },
    });
    return {
      clientVendor: data?.getCLIENTVENDORS,
      success: true,
      projectsUpdateResult,
    };
  }),
  createVendor: (vendorData) => async (dispatch) => {
    console.log('=== VENDOR CREATION START ===');
    console.log('vendorData:', JSON.stringify(vendorData, null, 2));
    
    // Add global error handler for this operation
    const originalErrorHandler = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
      console.error('Global error during vendor creation:', {
        message, source, lineno, colno, error,
        stack: error?.stack
      });
      return false;
    };
    
    try {
      
      if (vendorData.clientID) {
        const { name, useEntityName, entityName, email, additionalEmails, phone, address1, address2, logo, city, state, zip, defaultProject, insuranceAgent } =
          vendorData;
        let insuranceAgentResponse;

        if (insuranceAgent?.name && insuranceAgent?.email) {
          console.log('Creating insurance agent...');
          insuranceAgentResponse = await API.graphql({
            query: createINSURANCEAGENT,
            variables: {
              input: {
                ...insuranceAgent,
              },
            },
          });
          console.log('Insurance agent created:', insuranceAgentResponse);
        }
        
        const insuranceAgentID = insuranceAgentResponse?.data.createINSURANCEAGENT?.id;
        console.log('Creating vendor...');
        const { data } = await API.graphql({
          query: createVENDOR,
          variables: {
            input: {
              name,
              useEntityName,
              entityName,
              email,
              additionalEmails,
              phone,
              address1,
              address2,
              logo,
              city,
              state,
              zip,
              ...(insuranceAgentID && { vENDORInsuranceAgentId: insuranceAgentID }),
            },
          },
        });
        console.log('Vendor created:', data);

      if (data?.createVENDOR) {
        try {
          console.log('Creating CLIENTVENDORS with NOT_COMPLIANT status...');
          // Create the client vendor with default status
          const clientVendor = await API.graphql({
            query: createCLIENTVENDORS,
            variables: {
              input: {
                clientID: vendorData.clientID,
                vendorID: data.createVENDOR.id,
                status: Vendorstatus.NOT_COMPLIANT,
              },
            },
          });
          console.log('CLIENTVENDORS created with status:', clientVendor);

        if (clientVendor?.data?.createCLIENTVENDORS.id) {
          console.log('Adding vendor projects...');

          if (vendorData?.selectedProjects?.length) {
            console.log('Creating vendor projects:', vendorData.selectedProjects);
            await createVendorProjects(vendorData.selectedProjects, clientVendor.data.createCLIENTVENDORS.id);
          } else if (defaultProject) {
            console.log('Creating default project:', defaultProject.id);
            await createVendorProjects([defaultProject.id], clientVendor.data.createCLIENTVENDORS.id);
          }
        }

        console.log('Fetching updated vendors list...');
        await dispatch(actions.fetchVendors(vendorData.clientID));
        console.log('Vendor creation completed successfully');
        
        // Restore original error handler
        window.onerror = originalErrorHandler;
        
        return {
          payload: {
            success: true,
            vendorId: data.createVENDOR.id,
            clientVendorID: clientVendor?.data?.createCLIENTVENDORS?.id,
          },
        };
        } catch (clientVendorError) {
          console.error('Error in CLIENTVENDORS creation/update process:', clientVendorError);
          console.error('ClientVendor error details:', JSON.stringify(clientVendorError, null, 2));
          window.onerror = originalErrorHandler;
          throw clientVendorError;
        }
      } else {
        console.error('Failed to create VENDOR - no data returned');
        window.onerror = originalErrorHandler;
        throw new Error('Failed to create vendor');
      }
    } else {
      console.error('No clientID provided in vendorData');
      window.onerror = originalErrorHandler;
      throw new Error('No clientID provided');
    }
    } catch (overallError) {
      console.error('Overall error in createVendor:', overallError);
      console.error('Overall error details:', JSON.stringify(overallError, null, 2));
      console.error('Error stack:', overallError?.stack);
      window.onerror = originalErrorHandler;
      throw overallError;
    }
  },
  deleteVendor: createAsyncThunk("vendor/deleteVendor", async (clientVendor) => {
    if (clientVendor?.projects?.items.length > 0) {
      const deleteVendorProjectsResponse = await deleteVendorProjects(clientVendor.projects.items.map((project) => project.id));
      const failedProjectDeletion = deleteVendorProjectsResponse.find((response) => !response.success);
      if (failedProjectDeletion) {
        return {
          success: false,
          message: "Unsuccessful vendor deletion from project " + failedProjectDeletion,
          projectId: failedProjectDeletion,
        };
      }
    }

    const deleteVendorResponse = await API.graphql({
      query: deleteVENDOR,
      variables: {
        input: {
          id: clientVendor.vendor.id,
        },
      },
    });

    if (!deleteVendorResponse.data.deleteVENDOR) {
      return {
        success: false,
        message: "Unsuccessful vendor deletion",
      };
    }

    const deleteClientVendorResponse = await API.graphql({
      query: deleteCLIENTVENDORS,
      variables: {
        input: {
          id: clientVendor.id,
        },
      },
    });

    if (!deleteClientVendorResponse.data.deleteCLIENTVENDORS) {
      return {
        success: false,
        message: "Unsuccessful vendor deletion",
      };
    }

    return {
      success: true,
      message: "VENDOR HAS BEEN DELETED",
    };
  }),

  updateStarredVendor: createAsyncThunk("vendor/updateStarredVendor", async ({ id, starred }) => {
    const { data } = await API.graphql({
      query: updateStarredVendor,
      variables: {
        id,
        starred,
      },
    });
    return {
      id,
      starred,
      success: true,
    };
  }),
  updateVendorStatus: createAsyncThunk("vendor/updateVendorStatus", async ({ id, status }) => {
    const { data } = await API.graphql({
      query: updateVendorStatus,
      variables: { id, status },
    });
    return {
      id,
      status,
      success: true,
    };
  }),
  updateVendorNotificationStatus: createAsyncThunk("vendor/updateVendorNotificationStatus", async ({ id, notificationStatus }) => {
    const { data } = await API.graphql({
      query: updateVendorNotificationStatus,
      variables: { id, notificationStatus },
    });
    return {
      id,
      notificationStatus,
      success: true,
    };
  }),
  updateVendorCoverages: createAsyncThunk("vendor/updateVendorCoverages", async ({ clientVendor, selectedCoverages = [], currentCoverages = [] }) => {
    const updateList = [];
    const createList = [];
    // const deleteList = []
    // const existingCoverages = [...currentCoverages]
    selectedCoverages?.map((classCoverage) => {
      const coverageData = {
        ...classCoverage,
        clientVendorID: clientVendor.id,
      };
      if (typeof coverageData.limits === "object") {
        coverageData.limits = JSON.stringify(coverageData.limits);
      }
      delete coverageData.createdAt;
      delete coverageData.updatedAt;
      delete coverageData.index;
      (classCoverage.id ? updateList : createList).push(coverageData);
    });

    await Promise.all(
      updateList.map(async (toUpdate) => {
        await API.graphql({
          query: updateVENDORCOVERAGE,
          variables: {
            input: {
              ...toUpdate,
            },
          },
        });
        return Promise.resolve();
      })
    );
    await Promise.all(
      createList.map(async (toCreate) => {
        await API.graphql({
          query: createVENDORCOVERAGE,
          variables: {
            input: {
              ...toCreate,
            },
          },
        });
        return Promise.resolve();
      })
    );
    // await Promise.all(deleteList.map(async toDelete => {
    //   await API.graphql({
    //     query: deleteVENDORCOVERAGE,
    //     variables: {
    //       input: {
    //         ...toDelete
    //       }
    //     }
    //   })
    //   return Promise.resolve()
    // }))
    const { data } = await API.graphql({
      query: getCLIENTVENDORS,
      variables: {
        id: clientVendor.id,
      },
    });
    return {
      success: true,
      clientVendor: data?.getCLIENTVENDORS,
    };
  }),

  submitScanResultToCertificate:
    ({ certificateData, scanData }) =>
    async (dispatch) => {
      try {
        // Validate inputs
        if (!certificateData?.id) {
          console.error('submitScanResultToCertificate: Missing certificate ID', { certificateData });
          return {
            payload: {
              success: false,
              error: 'Certificate ID is missing'
            },
            error: { message: 'Certificate ID is missing' }
          };
        }

        if (!scanData?.id) {
          console.error('submitScanResultToCertificate: Missing scan data ID', { scanData });
          return {
            payload: {
              success: false,
              error: 'Scan data ID is missing'
            },
            error: { message: 'Scan data ID is missing' }
          };
        }

        if (certificateData.id !== scanData.certificateID) {
          console.error('submitScanResultToCertificate: Certificate ID mismatch', {
            certificateId: certificateData.id,
            scanCertificateId: scanData.certificateID
          });
          return {
            payload: {
              success: false,
              error: `Certificate ID mismatch: ${certificateData.id} !== ${scanData.certificateID}`
            },
            error: { message: `Certificate ID mismatch: ${certificateData.id} !== ${scanData.certificateID}` }
          };
        }

        console.log('submitScanResultToCertificate: Starting submission', {
          certificateId: certificateData.id,
          scanId: scanData.id
        });

        const currentCoverages = certificateData?.coverages?.items;
        const newCoverages = scanData?.coverages?.items;

        // move acord25 file over
        console.log('Copying scanned ACORD 25 file...');
        await copyScannedAcord25(certificateData.id, scanData.id, scanData.acord25File);

        // save the new certificate data
        const updates = {
          ...scanData,
          insured: scanData.insured || " ",
          producer: scanData.producer || " ",
          status: "REVIEW",
        };
        // Delete scan-specific fields that don't exist in CERTIFICATE schema
        delete updates.id;
        delete updates.coverages;
        delete updates.certificateID;
        delete updates.scanError;
        delete updates.scanStage;
        delete updates.files;
        delete updates.createdAt;
        delete updates.updatedAt;
        delete updates.__typename;
        delete updates._deleted;
        delete updates._lastChangedAt;
        delete updates._version;
        // Delete ACORD25SCAN-specific fields not in CERTIFICATE
        delete updates.uploadLink;
        delete updates.uploaderName;
        delete updates.uploaderEmail;
        delete updates.scanConfidence;
        delete updates.fieldConfidence;

        // save new coverages
        if (newCoverages?.length) {
          console.log(`Creating ${newCoverages.length} new coverages...`);
          updates.firstExp = getFirstExp(newCoverages);
          await Promise.all(
            newCoverages.map((newCoverage) => {
              delete newCoverage.id;
              delete newCoverage.acord25ScanID;
              delete newCoverage.__typename;
              delete newCoverage._deleted;
              delete newCoverage._lastChangedAt;
              delete newCoverage._version;
              delete newCoverage.createdAt;
              delete newCoverage.updatedAt;

              // Stringify limits and overrides if they are objects (required for AWSJSON type)
              const limits = newCoverage?.limits && typeof newCoverage.limits !== "string"
                ? JSON.stringify(newCoverage.limits)
                : newCoverage?.limits;
              const overrides = newCoverage?.overrides && typeof newCoverage.overrides !== "string"
                ? JSON.stringify(newCoverage.overrides)
                : newCoverage?.overrides;

              return API.graphql({
                query: createCERTIFICATECOVERAGE,
                variables: {
                  input: {
                    ...newCoverage,
                    certificateID: certificateData.id,
                    limits,
                    overrides,
                  },
                },
              });
            })
          );
        }

        // delete original coverages
        if (currentCoverages?.length) {
          console.log(`Deleting ${currentCoverages.length} old coverages...`);
          await Promise.all(
            currentCoverages.map((currentCoverage) => {
              return API.graphql({
                query: deleteCERTIFICATECOVERAGE,
                variables: {
                  input: {
                    id: currentCoverage.id,
                  },
                },
              });
            })
          );
        }

        // save the rest of the cert data
        console.log('Updating certificate data...');
        console.log('=== CERTIFICATE UPDATE PAYLOAD ===');
        console.log('Fields being sent:', Object.keys(updates));
        console.log('Full update object:', JSON.stringify(updates, null, 2));
        await API.graphql({
          query: updateCERTIFICATE,
          variables: {
            input: {
              ...updates,
              id: certificateData.id,
            },
          },
        });

        // update scan result to submitted
        console.log('Marking scan as SUBMITTED...');
        await API.graphql({
          query: updateACORD25SCAN,
          variables: {
            input: {
              id: scanData.id,
              status: "SUBMITTED",
            },
          },
        });

        // Add small delay to allow for database consistency before fetching
        console.log('Waiting for database consistency...');
        await new Promise(resolve => setTimeout(resolve, 1500));

        // refresh certificate data with cache invalidation
        console.log('Refreshing certificate data...');
        const refreshedCert = await API.graphql({
          query: getCERTIFICATE,
          variables: {
            id: certificateData.id,
          },
        });

        // Manually dispatch the result to Redux instead of using getCertificate action
        // This ensures we're not using cached data
        if (refreshedCert?.data?.getCERTIFICATE) {
          console.log('Refreshed certificate data:', refreshedCert.data.getCERTIFICATE);
          dispatch({
            type: 'vendor/getCertificate/fulfilled',
            payload: {
              success: true,
              certificate: refreshedCert.data.getCERTIFICATE
            }
          });
        }

        console.log('submitScanResultToCertificate: Success');
        return {
          payload: {
            success: true,
            certificateID: certificateData.id,
          },
        };
      } catch (error) {
        console.error('submitScanResultToCertificate: Error occurred', error);
        console.error('Error details:', {
          message: error?.message,
          errors: error?.errors,
          stack: error?.stack
        });

        const errorMessage = error?.errors?.[0]?.message || error?.message || 'Unknown error during scan result submission';
        return {
          payload: {
            success: false,
            error: errorMessage
          },
          error: {
            message: errorMessage,
            originalError: error
          }
        };
      }
    },
  createOrUpdateCertificate: createAsyncThunk(
    "vendor/createOrUpdateCertificate",
    async ({ certificateData, updatedFiles, updatedSupplementalCOIDocumentation = {}, coveragesToDelete = [] }, { dispatch, getState }) => {
      const data = {
        ...certificateData,
        insured: certificateData.insured || " ",
        producer: certificateData.producer || " ",
        ...(!certificateData.id && { acord25FileDate: usToAWSDate(new Date().toLocaleDateString("en-US")) }),
        ...(certificateData.overrides && {
          overrides: typeof certificateData.overrides === "string" ? certificateData.overrides : JSON.stringify(certificateData.overrides),
        }),
      };
      const coverages = data?.coverages?.items;
      let supplementalCoiFileUploadsAndDeletions;
      delete data.createdAt;
      delete data.updatedAt;
      if (coverages) {
        delete data.coverages;
      }
      if (updatedFiles?.length) {
        const files = updatedFiles
          ?.filter((updatedFile) => !updatedFile.isDeleted && !updatedFile.isAcord25 && updatedFile.name !== data.acord25File)
          ?.map((updatedFile) => updatedFile.name);
        data.files = files?.length ? files : null;
      }

      if (Object.keys(updatedSupplementalCOIDocumentation).length) {
        supplementalCoiFileUploadsAndDeletions = {
          upload: {},
          delete: {},
        };
        let newSupplementalCoiDocuments = {};

        if (data.supplementalCoiFiles && typeof data.supplementalCoiFiles === "string") {
          data.supplementalCoiFiles = JSON.parse(data.supplementalCoiFiles);
        }

        Object.entries(updatedSupplementalCOIDocumentation).forEach(([insuranceType, documents]) =>
          Object.entries(documents).forEach(([documentType, file]) => {
            if (file.isNew) {
              // Ensure that there is an object property "insuranceType" in the object
              newSupplementalCoiDocuments[insuranceType] = newSupplementalCoiDocuments[insuranceType] || {};
              if (data.supplementalCoiFiles && data.supplementalCoiFiles[insuranceType] && data.supplementalCoiFiles[insuranceType][documentType]) {
                // Ensure that there is an object property "insuranceType" in the object
                supplementalCoiFileUploadsAndDeletions["delete"][insuranceType] = supplementalCoiFileUploadsAndDeletions["delete"][insuranceType] || {};
                supplementalCoiFileUploadsAndDeletions["delete"][insuranceType][documentType] = data.supplementalCoiFiles[insuranceType][documentType];
              }
              newSupplementalCoiDocuments[insuranceType][documentType] = file.name;
              // Ensure that there is an object property "insuranceType" in the object
              supplementalCoiFileUploadsAndDeletions["upload"][insuranceType] = supplementalCoiFileUploadsAndDeletions["upload"][insuranceType] || {};
              supplementalCoiFileUploadsAndDeletions["upload"][insuranceType][documentType] = file;
            } else {
              // Ensure that there is an object property "insuranceType" in the object
              newSupplementalCoiDocuments[insuranceType] = newSupplementalCoiDocuments[insuranceType] || {};
              newSupplementalCoiDocuments[insuranceType][documentType] = file.name;
            }
          })
        );
        data.supplementalCoiFiles = JSON.stringify(newSupplementalCoiDocuments);
      }

      if (coverages?.length) {
        data.firstExp = getFirstExp(coverages);
      }

      const certificate = await API.graphql({
        query: data.id ? updateCERTIFICATE : createCERTIFICATE,
        variables: {
          input: data,
        },
      });
      const certificateID = data.id || certificate?.data?.createCERTIFICATE?.id;
      if (coverages?.length) {
        await Promise.all(
          coverages.map(async (coverage) => {
            delete coverage.index;
            const limits = coverage?.limits && typeof coverage.limits !== "string" ? JSON.stringify(coverage.limits) : coverage?.limits;
            const overrides = coverage?.overrides && typeof coverage.overrides !== "string" ? JSON.stringify(coverage.overrides) : coverage?.overrides;

            return await API.graphql({
              query: coverage.id ? updateCERTIFICATECOVERAGE : createCERTIFICATECOVERAGE,
              variables: {
                input: {
                  ...coverage,
                  certificateID,
                  limits,
                  overrides,
                },
              },
            });
          })
        );
      }
      if (coveragesToDelete?.length) {
        await Promise.all(
          coveragesToDelete.map((coverage) =>
            API.graphql({
              query: deleteCERTIFICATECOVERAGE,
              variables: {
                input: {
                  id: coverage.id,
                },
              },
            })
          )
        );
      }
      if (updatedFiles?.length) {
        await Promise.all(
          updatedFiles.map(async (updatedFile) => {
            if (updatedFile.isNew) {
              const identityId = updatedFile.isAcord25 ? "scanning" : "certificates";
              let scanData;
              if (updatedFile.isAcord25) {
                scanData = await API.graphql({
                  query: createACORD25SCAN,
                  variables: {
                    input: {
                      status: "PENDING",
                      certificateID,
                      acord25File: updatedFile.name,
                    },
                  },
                });
              }
              return uploadCertificateFile(certificateID, identityId, updatedFile.name, updatedFile.content, scanData?.data?.createACORD25SCAN?.id);
            } else if (updatedFile.isDeleted) {
              const filePath = updatedFile.isAcord25 ? `acord25/${updatedFile.name}` : updatedFile.name;
              return deleteCertificateFile(certificateID, filePath);
            }
            return Promise.resolve();
          })
        );
      }
      if (supplementalCoiFileUploadsAndDeletions) {
        await Promise.all(
          Object.keys(supplementalCoiFileUploadsAndDeletions.delete).flatMap(async (insuranceType) =>
            Object.entries(supplementalCoiFileUploadsAndDeletions["delete"][insuranceType]).map(async ([documentType, file]) => {
              console.log("deleting", insuranceType, documentType, file);
              return deleteCertificateSupplementalCoiFile(certificateID, insuranceType, documentType, file);
            })
          )
        );
        await Promise.all(
          Object.keys(supplementalCoiFileUploadsAndDeletions.upload).flatMap(async (insuranceType) =>
            Object.entries(supplementalCoiFileUploadsAndDeletions["upload"][insuranceType]).map(async ([documentType, file]) => {
              return uploadCertificateSupplementalCoiFile(certificateID, insuranceType, documentType, file.name, file.content);
            })
          )
        );
      }
      await dispatch(actions.getCertificate(certificateID));

      // Log certificate creation activity (only for new certificates, not updates)
      if (!data.id && certificateData.clientVendorID) {
        try {
          const state = getState();
          const loggedInUser = state.user?.loggedInUser;

          let userFullName = loggedInUser?.email || 'Unknown User';
          if (loggedInUser?.firstName || loggedInUser?.lastName) {
            userFullName = `${loggedInUser.firstName || ''}${loggedInUser.firstName ? " " : ""}${loggedInUser.lastName || ''}`.trim();
          }

          await logVendorActivity({
            clientVendorID: certificateData.clientVendorID,
            userFullName,
            userID: loggedInUser?.id || 'unknown',
            action: 'CERTIFICATE_CREATION',
            field: JSON.stringify({
              message: 'Certificate uploaded',
              details: {
                certificateID,
                insured: certificateData.insured || data.insured,
                producer: certificateData.producer || data.producer
              }
            })
          });

          console.log('Certificate creation activity logged for:', certificateID);
        } catch (activityError) {
          console.error('Failed to log certificate creation activity:', activityError);
          // Don't fail the whole operation if activity logging fails
        }
      }

      return {
        success: true,
        certificateID,
      };
    }
  ),
  deleteCertificate: (certificate) => async (dispatch) => {
    const { coverages, id: certificateID } = certificate;

    if (coverages?.items.length) {
      await Promise.all(
        coverages?.items.map((coverage) =>
          API.graphql({
            query: deleteCERTIFICATECOVERAGE,
            variables: {
              input: {
                id: coverage.id,
              },
            },
          })
        )
      );
    }

    await API.graphql({
      query: deleteCERTIFICATE,
      variables: {
        input: {
          id: certificateID,
        },
      },
    });
    await deleteCertificateFromS3(certificateID);
    return {
      payload: {
        success: true,
        certificateID,
      },
    };
  },
  getCertificate: createAsyncThunk("vendor/getCertificate", async (certificateId) => {
    const { data } = await API.graphql({
      query: getCERTIFICATE,
      variables: {
        id: certificateId,
      },
    });
    return {
      success: true,
      certificate: data?.getCERTIFICATE,
    };
  }),
  sendEmailToVendor: createAsyncThunk("vendor/sendEmailToVendor", async ({ uploadLink, ...rest }) => {
    const { data } = await API.graphql({
      query: createEMAILNOTIFICATIONS,
      variables: {
        input: {
          ...rest,
          ...(uploadLink ? { uploadLink } : {}),
        },
      },
    });
    if (data?.createEMAILNOTIFICATIONS) {
      return {
        success: true,
      };
    }
  }),
  createRequestCertificate: createAsyncThunk(
    "vendor/createRequestCertificate",
    async ({ clientVendorID, projectID, clientID, initialCoverageRequirements, initialDescriptionOfOperation, sentBy, sentTo }, { getState }) => {
      console.log("createRequestCertificate", sentBy, sentTo);
      try {
        const result = await API.graphql({
          query: createCERTIFICATE,
          variables: {
            input: {
              clientID,
              clientVendorID,
              status: "REQUESTED",
              initialCoverageRequirements,
              initialDescriptionOfOperation,
              ...(projectID ? { projectID } : {}),
              ...(sentBy ? { sentBy } : {}),
              ...(sentTo ? { sentTo } : {}),
            },
          },
        });

        if (!result?.data?.createCERTIFICATE?.id) {
          throw new Error('Failed to create certificate request');
        }

        const certificateId = result.data.createCERTIFICATE.id;

        // Log certificate request activity
        if (clientVendorID) {
          try {
            const state = getState();
            const loggedInUser = state.user?.loggedInUser;

            let userFullName = loggedInUser?.email || 'Unknown User';
            if (loggedInUser?.firstName || loggedInUser?.lastName) {
              userFullName = `${loggedInUser.firstName || ''}${loggedInUser.firstName ? " " : ""}${loggedInUser.lastName || ''}`.trim();
            }

            await logVendorActivity({
              clientVendorID,
              userFullName,
              userID: loggedInUser?.id || 'unknown',
              action: 'REQUEST_CERTIFICATE',
              field: JSON.stringify({
                message: 'Certificate request sent',
                details: {
                  certificateID: certificateId,
                  sentTo: Array.isArray(sentTo) ? sentTo : [sentTo],
                  projectID: projectID || null
                }
              })
            });

            console.log('Certificate request activity logged for:', certificateId);
          } catch (activityError) {
            console.error('Failed to log certificate request activity:', activityError);
            // Don't fail the whole operation if activity logging fails
          }
        }

        return {
          success: true,
          certificateId,
        };
      } catch (error) {
        console.error('Error in createRequestCertificate:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        // If it's a GraphQL error, extract the message
        const errorMessage = error?.errors?.[0]?.message || error?.message || 'Failed to create certificate request';
        throw new Error(errorMessage);
      }
    }
  ),
  getRequestCertificates: createAsyncThunk("vendor/getRequestCertificates", async ({ clientVendorID, projectID, clientID }) => {
    const existing = await API.graphql({
      query: certificateByClientVendor,
      variables: {
        clientVendorID,
        filter: {
          projectID: { ...(projectID ? { eq: projectID } : { attributeExists: false }) },
          status: { eq: "REQUESTED" },
        },
      },
    });
    return {
      success: true,
      certificateId: existing.data.certificateByClientVendor?.items,
    };
  }),
  getAllVendorClientCertificates: createAsyncThunk("vendor/getAllVendorClientCertificates", async ({ clientID, clientVendorID }) => {
    let nextToken = null;
    let runningCount = 0;
    let initialRun = true;
    let certificates = [];
    do {
      const { data } = await API.graphql({
        query: certificateByClient,
        variables: {
          clientID,
          nextToken,
          filter: {
            // status: { ne: "REQUESTED" },
            clientVendorID: { eq: clientVendorID },
          },
        },
      });
      initialRun = false;
      if (data?.certificateByClient?.items?.length) {
        runningCount += data.certificateByClient.items.length;
        certificates = certificates.concat(data.certificateByClient.items);
      }
      nextToken = data?.certificateByClient?.nextToken || null;
    } while (initialRun || nextToken);

    certificates.sort((a, b) => {
      let larger;
      if (a.updatedAt && b.updatedAt) {
        larger = new Date(a.updatedAt).getTime() > new Date(b.updatedAt).getTime();
      }
      if (larger) {
        return -1;
      }
      return 1;
    });

    return {
      success: true,
      certificatesCount: runningCount,
      certificates,
    };
  }),
};

export const vendorSlice = createSlice({
  name: "vendor",
  initialState,
  reducers: {
    resetState: (state, action) => {
      state.vendors = null
      state.certificates = {}
      state.vendorCertificates = null;
      state.paginatedVendorCertificates = null;
      state.vendorCertificatesCount = 0;
    },
  },
  extraReducers: builder => {
    builder
      // fetching vendors
      .addCase(actions.fetchVendors.pending, statusLoading)
      .addCase(actions.fetchVendors.fulfilled, (state, action) => {
        state.status = 'success'
        state.vendors = action.payload.vendors
      })
      .addCase(actions.fetchVendors.rejected, statusFailure)
      // fetching vendor
      .addCase(actions.fetchVendor.pending, statusLoading)
      .addCase(actions.fetchVendor.fulfilled, updateOneVendor)
      .addCase(actions.fetchVendor.rejected, statusFailure)
      // update vendor
      .addCase(actions.updateVendor.pending, statusLoading)
      .addCase(actions.updateVendor.fulfilled, updateOneVendor)
      .addCase(actions.updateVendor.rejected, statusFailure)
      // update starred vendor
      .addCase(actions.updateStarredVendor.pending, statusLoading)
      .addCase(actions.updateStarredVendor.fulfilled, (state, action) => {
        state.status = 'success'
        state.vendors = state.vendors.map(oneVendor => {
          if(oneVendor.id === action?.payload?.id) {
            oneVendor.starred = action.payload.starred
          }
          return oneVendor
        })
      })
      .addCase(actions.updateStarredVendor.rejected, statusFailure)
      // update vendor status
      .addCase(actions.updateVendorStatus.pending, statusLoading)
      .addCase(actions.updateVendorStatus.fulfilled, (state, action) => {
        state.status = 'success'
        state.vendors = state.vendors.map(oneVendor => {
          if(oneVendor.id === action?.payload?.id) {
            oneVendor.status = action.payload.status
          }
          return oneVendor
        })
      })
      .addCase(actions.updateVendorStatus.rejected, statusFailure)
      // update vendor nofitication status
      .addCase(actions.updateVendorNotificationStatus.pending, statusLoading)
      .addCase(actions.updateVendorNotificationStatus.fulfilled, (state, action) => {
        state.status = 'success'
        state.vendors = state.vendors.map(oneVendor => {
          if (oneVendor.id === action?.payload?.id) {
            oneVendor.notificationStatus = action.payload.notificationStatus
          }
          return oneVendor
        })
      })
      .addCase(actions.updateVendorNotificationStatus.rejected, statusFailure)
      //update Vendor Coverages
      .addCase(actions.updateVendorCoverages.pending, statusLoading)
      .addCase(actions.updateVendorCoverages.fulfilled, updateOneVendor)
      .addCase(actions.updateVendorCoverages.rejected, statusFailure)
      //get certificate
      .addCase(actions.getCertificate.pending, statusLoading)
      .addCase(actions.getCertificate.fulfilled, (state, action) => {
        state.certificates = {
          ...state.certificates,
          [action.payload?.certificate?.id]: action.payload?.certificate
        }
        const updatedCertificateIndex = state.vendorCertificates?.findIndex(cert => cert.id === action.payload?.certificate?.id)

        if (updatedCertificateIndex > -1) {
          state.vendorCertificates[updatedCertificateIndex] = action.payload?.certificate
        }
      })
      .addCase(actions.getCertificate.rejected, statusFailure)
      //update certificate
      .addCase(actions.createOrUpdateCertificate.pending, statusLoading)
      .addCase(actions.createOrUpdateCertificate.fulfilled, (state, action) => {
        state.status = "success"
        //refresh vendor certificate's on updated or created certificate
        state.vendorCertificates = [...state.vendorCertificates || []]
      })
      // .addCase(actions.createOrUpdateCertificate.rejected, statusFailure)
      // get all vendor certificates
      .addCase(actions.getAllVendorClientCertificates.fulfilled, (state, action) => {
        state.vendorCertificates = action.payload.certificates;
        state.vendorCertificatesCount = action.payload.certificatesCount;
      })
      // create request certificate
      .addCase(actions.createRequestCertificate.pending, statusLoading)
      .addCase(actions.createRequestCertificate.fulfilled, (state, action) => {
        state.status = 'success'
      })
      .addCase(actions.createRequestCertificate.rejected, statusFailure)
  }
})

export const vendorState = state => state.vendor

export const vendorCertificatesState = createSelector(
  [state => state.vendor.vendorCertificates, state => state.vendor.vendorCertificatesCount],
  (vendorCertificates, vendorCertificatesCount) => ({
    vendorCertificates,
    vendorCertificatesCount
  })
)

export const certificateState = (certificateId) => createSelector(
  [state => state.vendor.certificates[certificateId]],
  (certificate) => certificate
)
export const vendorActions = {
  ...vendorSlice.actions,
  ...actions
}

export default vendorSlice.reducer
